import { useMeQuery } from '../api/api.portal';
import { BUSINESS_USER_ROLE, ManagerPermission } from '../constants/managerPermissions';
import { useMemo } from 'react';

/**
 * Manager permission gate for the web UI.
 *
 * `isManager` is true only for a sub-account whose /me role is 'manager'. For everyone else —
 * business owners, admins, regular streamers — `has()` returns true so existing call sites that
 * wrap owner-only controls keep working without a role check.
 *
 * NOTE: this is UI convenience only. Streaming-action rights (channels, start/stop, overlays,
 * chat) are enforced here alone; the server enforces streamer assignment and owner-level actions.
 */
export function useManagerPermissions() {
  const { data: profile } = useMeQuery();

  return useMemo(() => {
    const isManager = profile?.role === BUSINESS_USER_ROLE.Manager;
    const permissions = (profile?.permissions || []) as ManagerPermission[];

    return {
      isManager,
      permissions,
      has: (permission: ManagerPermission) => !isManager || permissions.includes(permission),
      // True when the manager holds at least one right from the group. Used to hide whole
      // sections that would otherwise render with every control disabled.
      hasAny: (group: ManagerPermission[]) =>
        !isManager || group.some(permission => permissions.includes(permission)),
    };
  }, [profile?.role, profile?.permissions]);
}
